import { ChecklistTemplate } from './types';

/**
 * Plantilla de revisión para Sistema de Evacuación de Humos (Exutorios / SCTEH).
 *
 * Referencias normativas:
 *   - RD 513/2017 (RIPCI), Anexo II.
 *   - UNE-EN 12101-2 (aireadores naturales).
 *   - UNE 23585 (sistemas de control de temperatura y evacuación de humos).
 *   - CTE DB-SI 3.
 *
 * Periodicidad:
 *   T = Trimestral
 *   A = Anual
 */
export const EXUTORIOS_TEMPLATE: ChecklistTemplate = {
  tipo_sistema: 'pci_exutorios',
  titulo: 'Sistema de Evacuación de Humos (Exutorios)',
  protocolos: [
    {
      id: 'inventario',
      titulo: '1. Inventario y configuración general',
      cabecera: [
        { key: 'num_exutorios', label: 'Nº de exutorios', tipo: 'number' },
        { key: 'tipo_aireador', label: 'Tipo (lamas / cúpula / mixto)' },
        { key: 'superficie_geometrica', label: 'Superficie geométrica total (m²)' },
        { key: 'superficie_util', label: 'Superficie útil aerodinámica (m²)' },
        { key: 'sector_humos', label: 'Sector de humos / referencia' },
      ],
      preguntas: [
        { id: 'IN.01', texto: '¿El inventario físico coincide con planos y proyecto técnico?', periodicidad: ['A'] },
        { id: 'IN.02', texto: '¿La superficie útil aerodinámica del sector cumple con lo proyectado?', periodicidad: ['A'] },
        { id: 'IN.03', texto: '¿Cada exutorio dispone de etiqueta de revisión visible y legible?', periodicidad: ['T','A'], unidades: true },
        { id: 'IN.04', texto: '¿Existe esquema de zonificación de humos visible en sala técnica?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'central',
      titulo: '2. Central de control de humos',
      cabecera: [
        { key: 'marca_central', label: 'Marca central' },
        { key: 'modelo_central', label: 'Modelo central' },
        { key: 'tension_alimentacion', label: 'Tensión alimentación (V)' },
        { key: 'baterias_v', label: 'Baterías (V/Ah)' },
        { key: 'fecha_baterias', label: 'Fecha baterías', tipo: 'date' },
      ],
      preguntas: [
        { id: 'CE.01', texto: '¿La central está alimentada y sin avisos de avería ni desconexión?', periodicidad: ['T','A'] },
        { id: 'CE.02', texto: '¿Los indicadores luminosos y zumbador funcionan correctamente?', periodicidad: ['T','A'] },
        { id: 'CE.03', texto: '¿Se ha probado la conmutación a baterías cortando la red 230 V?', periodicidad: ['A'] },
        { id: 'CE.04', texto: '¿La tensión y autonomía de las baterías cumple lo exigido (≥ 72 h reposo + 30 min maniobra)?', periodicidad: ['A'] },
        { id: 'CE.05', texto: '¿Los pulsadores manuales de apertura/cierre y RESET responden correctamente?', periodicidad: ['T','A'], unidades: true },
        { id: 'CE.06', texto: '¿La señalización de zona/sector en la central es coherente con la instalación?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'exutorios',
      titulo: '3. Estado de los exutorios (cúpulas / lamas)',
      preguntas: [
        { id: 'EX.01', texto: '¿La cúpula, lamas o lucernario están íntegros, sin roturas, opacidad ni deformaciones?', periodicidad: ['T','A'], unidades: true },
        { id: 'EX.02', texto: '¿Los herrajes, bisagras y mecanismos están sin oxidación y correctamente engrasados?', periodicidad: ['A'], unidades: true },
        { id: 'EX.03', texto: '¿La estanqueidad en reposo es correcta (no hay entrada de agua ni aire)?', periodicidad: ['A'], unidades: true },
        { id: 'EX.04', texto: '¿La apertura mecánica alcanza el ángulo nominal (≥ 140° o el indicado por fabricante)?', periodicidad: ['A'], unidades: true },
        { id: 'EX.05', texto: '¿El cierre tras la prueba se produce correctamente sin esfuerzo manual?', periodicidad: ['A'], unidades: true },
        { id: 'EX.06', texto: '¿La zona del exutorio está libre de obstáculos (cableados, instalaciones, mercancía)?', periodicidad: ['T','A'], unidades: true },
      ],
    },
    {
      id: 'botellines',
      titulo: '4. Botellines de impulsión (CO₂ / N₂)',
      cabecera: [
        { key: 'tipo_gas', label: 'Tipo de gas (CO2 / N2)' },
        { key: 'presion_botellin', label: 'Presión botellín (bar)' },
      ],
      preguntas: [
        { id: 'BI.01', texto: '¿El botellín está correctamente fijado, sin golpes ni oxidación visible?', periodicidad: ['T','A'], unidades: true },
        { id: 'BI.02', texto: '¿La presión indicada en el manómetro está dentro del rango admisible del fabricante?', periodicidad: ['T','A'], unidades: true },
        { id: 'BI.03', texto: '¿El peso del botellín coincide con el peso de carga ± tolerancia (≤ 10 %)?', periodicidad: ['A'], unidades: true },
        { id: 'BI.04', texto: '¿Está dentro de plazo el retimbrado quinquenal (ITC-MIE-AP7, cada 5 años)?', periodicidad: ['A'], unidades: true, nota: 'Próximo retimbre calculado automáticamente.' },
        { id: 'BI.05', texto: '¿La vida útil del botellín está dentro de los 20 años desde fabricación?', periodicidad: ['A'], unidades: true },
        { id: 'BI.06', texto: '¿La conexión a la electroválvula / actuador es estanca y sin fugas?', periodicidad: ['A'], unidades: true },
        { id: 'BI.07', texto: '¿La etiqueta identificativa (gas, carga, fechas, eficacia) es legible?', periodicidad: ['T','A'], unidades: true },
      ],
    },
    {
      id: 'compresor',
      titulo: '5. Compresor y red neumática',
      cabecera: [
        { key: 'presion_servicio', label: 'Presión de servicio (bar)' },
        { key: 'presion_corte', label: 'Presión de corte (bar)' },
      ],
      preguntas: [
        { id: 'CP.01', texto: '¿El compresor arranca y para automáticamente dentro de los rangos de presión configurados?', periodicidad: ['T','A'] },
        { id: 'CP.02', texto: '¿El nivel/condición del aceite (si procede) y filtros están en buen estado?', periodicidad: ['A'] },
        { id: 'CP.03', texto: '¿Se ha purgado el calderín y el separador de condensados?', periodicidad: ['T','A'] },
        { id: 'CP.04', texto: '¿La red de aire comprimido está sin fugas (test de caída de presión)?', periodicidad: ['A'] },
        { id: 'CP.05', texto: '¿La tensión, potencia y caudal del compresor coinciden con la placa de características?', periodicidad: ['A'] },
        { id: 'CP.06', texto: '¿El presostato y la válvula de seguridad funcionan correctamente?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'sondas',
      titulo: '6. Sondas de aire y lluvia',
      preguntas: [
        { id: 'SO.01', texto: '¿La sonda de viento/lluvia está libre de obstáculos, suciedad y nidos?', periodicidad: ['T','A'], unidades: true },
        { id: 'SO.02', texto: '¿La sonda responde al simular lluvia (cierre automático de exutorios de ventilación)?', periodicidad: ['A'], unidades: true },
        { id: 'SO.03', texto: '¿La sonda responde al simular viento alto (cierre automático)?', periodicidad: ['A'], unidades: true },
        { id: 'SO.04', texto: '¿El cableado de las sondas está protegido frente a intemperie y sin daños?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'maniobra',
      titulo: '7. Maniobras y prueba de funcionamiento',
      preguntas: [
        { id: 'MN.01', texto: '¿La maniobra de apertura por pulsador manual abre todos los exutorios del sector en ≤ 60 s?', periodicidad: ['A'] },
        { id: 'MN.02', texto: '¿La maniobra por alarma de incendios (señal de la central CDI) dispara correctamente la apertura?', periodicidad: ['A'] },
        { id: 'MN.03', texto: '¿La maniobra de ventilación diaria (si existe) abre y cierra correctamente los exutorios?', periodicidad: ['T','A'] },
        { id: 'MN.04', texto: '¿El sistema realiza el cierre tras RESET sin que queden exutorios trabados?', periodicidad: ['A'] },
        { id: 'MN.05', texto: '¿La aireación lateral / aportación de aire complementaria funciona simultáneamente?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'documentacion',
      titulo: '8. Documentación y registros',
      preguntas: [
        { id: 'DC.01', texto: '¿Existe acta firmada de la última revisión anual por empresa mantenedora habilitada?', periodicidad: ['A'] },
        { id: 'DC.02', texto: '¿Se conservan los certificados de retimbrado quinquenal de los botellines?', periodicidad: ['A'] },
        { id: 'DC.03', texto: '¿Está actualizado el inventario con marca, modelo, ubicación, NºSerie y fechas de retimbre/caducidad?', periodicidad: ['A'] },
        { id: 'DC.04', texto: '¿Se ha entregado al titular el informe de anomalías y acciones correctoras?', periodicidad: ['A'] },
      ],
    },
  ],
};
